import { useEffect, useRef, useState } from "react"
import { Check, Pencil, Trash2, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "../Button/Button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../Popover/Popover"

export interface PublishedNoteEditorProps {
  note: string
  multiLine?: boolean
  placeholder?: string
  viewEmptyText?: string
  editorPlaceholder?: string
  editHintText?: string
  noteHeader?: string
  editHeader?: string
  saveAriaLabel?: string
  cancelAriaLabel?: string
  addAriaLabel?: string
  editAriaLabel?: string
  deleteAriaLabel?: string
  onChange?: (note: string) => void
  className?: string
}

export function PublishedNoteEditor({
  note,
  multiLine = false,
  placeholder = "Add a note…",
  viewEmptyText = "No note yet. Click the pencil to add one.",
  editorPlaceholder = "Write a note about this deployment…",
  editHintText = "⌘/Ctrl + Enter to save · Esc to cancel",
  noteHeader = "Note",
  editHeader = "Editing",
  saveAriaLabel = "Save note",
  cancelAriaLabel = "Cancel",
  addAriaLabel = "Add note",
  editAriaLabel = "Edit note",
  deleteAriaLabel = "Delete note",
  onChange,
  className,
}: PublishedNoteEditorProps) {
  const [open, setOpen] = useState(false)
  const hasNote = note.length > 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn("block min-w-0 max-w-full text-left cursor-pointer group", className)}
        >
          {hasNote ? (
            <span
              className={cn(
                "text-caption text-foreground group-hover:text-foreground",
                multiLine ? "line-clamp-2 break-words" : "block truncate",
              )}
            >
              {note}
            </span>
          ) : (
            <span className="text-caption text-muted-foreground italic inline-flex items-center gap-1.5">
              <Pencil size={11} strokeWidth={1.75} className="shrink-0" />
              {placeholder}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="v1 w-[min(20rem,calc(100vw-2rem))] p-0 overflow-hidden"
      >
        <PublishedNoteEditorContent
          note={note}
          onChange={onChange}
          onClose={() => setOpen(false)}
          viewEmptyText={viewEmptyText}
          editorPlaceholder={editorPlaceholder}
          editHintText={editHintText}
          noteHeader={noteHeader}
          editHeader={editHeader}
          saveAriaLabel={saveAriaLabel}
          cancelAriaLabel={cancelAriaLabel}
          addAriaLabel={addAriaLabel}
          editAriaLabel={editAriaLabel}
          deleteAriaLabel={deleteAriaLabel}
        />
      </PopoverContent>
    </Popover>
  )
}

export interface PublishedNoteEditorContentProps {
  note: string
  onChange?: (note: string) => void
  onClose?: () => void
  initialMode?: "view" | "edit"
  viewEmptyText?: string
  editorPlaceholder?: string
  editHintText?: string
  noteHeader?: string
  editHeader?: string
  saveAriaLabel?: string
  cancelAriaLabel?: string
  addAriaLabel?: string
  editAriaLabel?: string
  deleteAriaLabel?: string
  className?: string
}

export function PublishedNoteEditorContent({
  note,
  onChange,
  onClose,
  initialMode = "view",
  viewEmptyText = "No note yet. Click the pencil to add one.",
  editorPlaceholder = "Write a note about this deployment…",
  editHintText = "⌘/Ctrl + Enter to save · Esc to cancel",
  noteHeader = "Note",
  editHeader = "Editing",
  saveAriaLabel = "Save note",
  cancelAriaLabel = "Cancel",
  addAriaLabel = "Add note",
  editAriaLabel = "Edit note",
  deleteAriaLabel = "Delete note",
  className,
}: PublishedNoteEditorContentProps) {
  const hasNote = note.length > 0
  const [mode, setMode] = useState<"view" | "edit">(initialMode)
  const [draft, setDraft] = useState(note)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setDraft(note)
  }, [note])

  useEffect(() => {
    if (mode === "edit") {
      requestAnimationFrame(() => {
        inputRef.current?.focus()
        inputRef.current?.select()
      })
    }
  }, [mode])

  const commit = () => {
    onChange?.(draft.trim())
    setMode("view")
    onClose?.()
  }
  const clearNote = () => {
    onChange?.("")
    setMode("view")
    setDraft("")
    onClose?.()
  }

  if (mode === "view") {
    return (
      <div className={cn("flex flex-col", className)}>
        <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2">
          <span className="text-caption uppercase tracking-wider text-muted-foreground">
            {noteHeader}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground [&_svg]:size-3"
            onClick={() => {
              setDraft(note)
              setMode("edit")
            }}
            aria-label={hasNote ? editAriaLabel : addAriaLabel}
          >
            <Pencil size={12} strokeWidth={1.75} />
          </Button>
        </div>
        <div className="px-4 pb-4">
          {hasNote ? (
            <p className="text-body text-foreground leading-relaxed whitespace-pre-wrap break-words">
              {note}
            </p>
          ) : (
            <p className="text-body text-muted-foreground italic">{viewEmptyText}</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-2">
        <span className="text-caption uppercase tracking-wider text-muted-foreground">
          {editHeader}
        </span>
        <div className="flex items-center gap-0.5">
          {hasNote && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground [&_svg]:size-3"
              onClick={clearNote}
              aria-label={deleteAriaLabel}
            >
              <Trash2 size={12} strokeWidth={1.75} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground [&_svg]:size-3"
            onClick={() => {
              setDraft(note)
              setMode("view")
            }}
            aria-label={cancelAriaLabel}
          >
            <X size={12} strokeWidth={1.75} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-primary hover:text-primary [&_svg]:size-3"
            onClick={commit}
            aria-label={saveAriaLabel}
          >
            <Check size={12} strokeWidth={2} />
          </Button>
        </div>
      </div>
      <div className="px-4 pb-3">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              commit()
            }
            if (e.key === "Escape") {
              e.preventDefault()
              setDraft(note)
              setMode("view")
            }
          }}
          placeholder={editorPlaceholder}
          className="flex min-h-20 w-full rounded-md border border-input bg-transparent px-3 py-2 text-body leading-relaxed shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 resize-none"
        />
        <p className="text-caption uppercase tracking-wider text-muted-foreground mt-2">
          {editHintText}
        </p>
      </div>
    </div>
  )
}
